/* eslint-disable prettier/prettier */
import React from "react"
import PropTypes from "prop-types"

// Components
import Gauge from "../components/Gauge/Gauge"
import StackedBarChart from "../components/StackedBarChart/StackedBarChart"
import TopTeams from "../components/TopTeams/TopTeams"
import ScoreDetails from "../components/ScoreDetails/ScoreDetails"
import resizeComponent from "../components/ResizeComponent/ResizeComponent"
import ErrorState from "../components/ErrorState/ErrorState"

// Resizable Components
const ResizableGauge = resizeComponent(Gauge)
const ResizableStackedBarChart = resizeComponent(StackedBarChart)

// Helper Functions
const getStackedWidths = datasetLength => ({
  curr: `1 1 ${Math.floor((1 / datasetLength) * 100) + 15}%`,
  historical: `1 1 ${Math.floor(((datasetLength - 1) / datasetLength) * 100) -
    15}%`
})

const Summary = props => {
  // Props
  const {
    detail,
    topOverallScore,
    topDepartments,
    riskDistributions,
    riskDistributions: { length },
    level,
    scoreDetails
  } = props

  const { curr, historical } = getStackedWidths(length)

  return (
    <div className="d-flex flex-row justify-content-between mt-3">
      <div
        className="min-width-0 d-flex flex-column justify-content-between"
        id="summary-div-1"
      >
        <h3 className="font-weight-bold text-truncate pb-3">Security Score</h3>
        <div className="mb-4 demo-tour-step-2">
          {topOverallScore ? (
            <ResizableGauge score={topOverallScore} />
          ) : (
            <div className="center-text">
              <ErrorState />
            </div>
          )}
        </div>
      </div>

      <div
        className="min-width-0 d-flex flex-column justify-content-between"
        id="summary-div-2"
      >
        <h3 className="font-weight-bold text-truncate pb-3">
          { level === "Department" ? <span className="demo-tour-step-8">Trending</span> : "Trending"}
        </h3>
        {length ? (
          <div className="w-100 d-flex">
            <div style={{ flex: historical }} className="min-width-0">
              <ResizableStackedBarChart
                data={riskDistributions.slice(0, -1)}
                containerClasses="min-width-0"
                containerId="stacked-historical"
                uniqueId="historical"
              />
            </div>
            <div style={{ flex: curr }} className="min-width-0">
              <ResizableStackedBarChart
                data={riskDistributions.slice(-1)}
                containerClasses="min-width-0"
                containerId="stacked-curr"
                uniqueId="curr"
                margin={{ top: 0, bottom: 35, left: 3, right: 0 }}
                isCurrentMonth
              />
            </div>
          </div>
        ) : (
          <div className="center-text error-bottom-margin">
            <ErrorState />
          </div>
        )}
      </div>

      <div className="min-width-0 d-flex flex-column" id="summary-div-3">
        <h3 className="font-weight-bold text-truncate pb-3">
          {level === "Company" ? "Weakest Departments" : `${level} Scores`}
        </h3>
        {(!detail && topDepartments.length > 1) || (detail && scoreDetails.length) ? (
          <div className="d-flex flex-column justify-content-center">
            {!detail && <TopTeams data={topDepartments} />}
            {detail && <ScoreDetails data={scoreDetails} />}
          </div>
        ) : (
          <div className="center-text">
            <ErrorState />
          </div>
        )}
      </div>
    </div>
  )
}

export default Summary

Summary.propTypes = {
  topOverallScore: PropTypes.number,
  topDepartments: PropTypes.arrayOf(
    PropTypes.shape({
      department: PropTypes.string,
      elevate_person_id: PropTypes.string,
      riskScore: PropTypes.number,
      employee_first_name: PropTypes.string,
      employee_last_name: PropTypes.string
    })
  ),
  riskDistributions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  detail: PropTypes.bool,
  level: PropTypes.string.isRequired,
  scoreDetails: PropTypes.arrayOf(
    PropTypes.shape({
      behavior: PropTypes.string,
      score: PropTypes.number
    })
  )
}

Summary.defaultProps = {
  topOverallScore: null,
  topDepartments: [],
  detail: false,
  scoreDetails: []
}
